/**
 * MCP Tools Index
 *
 * Exports all available MCP tools for the EnergyPlus IDF assistant.
 * These tools run client-side and interact with the app's state stores.
 */

export { readTools } from './readTools';
export { writeTools } from './writeTools';
export { queryTools } from './queryTools';
export { validationTools } from './validationTools';
export { simulationTools } from './simulationTools';

import { readTools } from './readTools';
import { writeTools } from './writeTools';
import { queryTools } from './queryTools';
import { validationTools } from './validationTools';
import { simulationTools } from './simulationTools';
import type { MCPTool } from '../types';

/**
 * All available MCP tools (excluding simulation tools for separate handling)
 */
export const allTools: MCPTool[] = [
  ...readTools,
  ...writeTools,
  ...queryTools,
  ...validationTools,
];

/**
 * All tools including simulation tools
 */
export const allToolsWithSimulation: MCPTool[] = [
  ...readTools,
  ...writeTools,
  ...queryTools,
  ...validationTools,
  ...simulationTools,
];

/**
 * Get tool definitions for Claude API (excluding simulation tools)
 */
export function getToolDefinitions(): Array<{
  name: string;
  description: string;
  input_schema: {
    type: 'object';
    properties: Record<string, unknown>;
    required?: string[];
  };
}> {
  return allTools.map((tool) => {
    const base = {
      name: tool.name,
      description: tool.description,
      input_schema: {
        type: 'object' as const,
        properties: tool.inputSchema.properties as Record<string, unknown>,
      },
    };
    // Only include required if it exists (exactOptionalPropertyTypes)
    if (tool.inputSchema.required && tool.inputSchema.required.length > 0) {
      return {
        ...base,
        input_schema: {
          ...base.input_schema,
          required: tool.inputSchema.required,
        },
      };
    }
    return base;
  });
}

/**
 * Get all tool definitions including simulation tools for Claude API
 */
export function getAllToolDefinitions(): Array<{
  name: string;
  description: string;
  input_schema: {
    type: 'object';
    properties: Record<string, unknown>;
    required?: string[];
  };
}> {
  return allToolsWithSimulation.map((tool) => {
    const base = {
      name: tool.name,
      description: tool.description,
      input_schema: {
        type: 'object' as const,
        properties: tool.inputSchema.properties as Record<string, unknown>,
      },
    };
    // Only include required if it exists (exactOptionalPropertyTypes)
    if (tool.inputSchema.required && tool.inputSchema.required.length > 0) {
      return {
        ...base,
        input_schema: {
          ...base.input_schema,
          required: tool.inputSchema.required,
        },
      };
    }
    return base;
  });
}

/**
 * Get a tool by name
 */
export function getTool(name: string): MCPTool | undefined {
  return allTools.find((t) => t.name === name);
}

/**
 * Get a tool by name (including simulation tools)
 */
export function getToolWithSimulation(name: string): MCPTool | undefined {
  return allToolsWithSimulation.find((t) => t.name === name);
}
